import React, { useState, useEffect, useMemo } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';
import { downloadInvoicePDF } from '../../utils/pdfService';
import SaleForm from '../../components/sales/SaleForm';
import SaleDetail from '../../components/sales/SaleDetail';
import { useAppData } from '../../contexts/AppDataContext';
import { transformSalesData, calculateSalesStats } from '../../utils/salesDataUtils';
import BarcodeScanner from '../../components/common/BarcodeScanner';

const SalesPage = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [salesPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { getSales, getCustomers } = useAppData();

  const getEntityName = (ent) => {
    if (!ent) return '';
    const full = `${ent.firstName || ''} ${ent.lastName || ''}`.trim();
    if (full) return full;
    if (ent.name && ent.name.trim() !== '') return ent.name;
    if (ent.company && ent.company.trim() !== '') return ent.company;
    return String(ent.id || '');
  };

  const formatAmount = (num) => `${Number(num || 0).toLocaleString()} DA`;
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch {
      return iso;
    }
  };

  useEffect(() => {
    // Vérifier si l'URL contient le paramètre scan=true
    const params = new URLSearchParams(location.search);
    setIsScannerOpen(params.get('scan') === 'true');
  }, [location.search]);

  // Gérer le téléchargement du PDF avec des données réelles
  const handleDownloadPDF = (sale) => {
    try {
      // Récupérer les données complètes de la vente depuis le contexte
      const allSales = getSales();
      const fullSaleData = allSales.find(s => s.id === sale.id) || sale;
      
      // Récupérer les données du client depuis le contexte
      const allCustomers = getCustomers();
      const customerData = allCustomers.find(c => c.id === fullSaleData.customerId) || {};
      
      // Préparer les données pour le PDF
      const invoiceData = {
        invoiceNumber: fullSaleData.invoiceNumber || fullSaleData.id,
        issueDate: fullSaleData.date || fullSaleData.createdAt,
        items: fullSaleData.items || [{
          description: 'Produit vendu',
          quantity: 1,
          price: parseFloat(String(fullSaleData.amount || fullSaleData.total || '0').replace(/[^0-9.-]+/g,'')),
          total: parseFloat(String(fullSaleData.amount || fullSaleData.total || '0').replace(/[^0-9.-]+/g,''))
        }],
        subtotal: parseFloat(String(fullSaleData.subtotal || fullSaleData.amount || fullSaleData.total || '0').replace(/[^0-9.-]+/g,'')),
        discount: parseFloat(String(fullSaleData.discount || '0').replace(/[^0-9.-]+/g,'')),
        tax: parseFloat(String(fullSaleData.tax || '0').replace(/[^0-9.-]+/g,'')),
        total: parseFloat(String(fullSaleData.total || fullSaleData.amount || '0').replace(/[^0-9.-]+/g,'')),
      };

      const formattedCustomerData = {
        firstName: customerData.firstName || sale.customer.split(' ')[0] || 'Client',
        lastName: customerData.lastName || sale.customer.split(' ').slice(1).join(' ') || '',
        email: customerData.email || 'Non spécifié',
        phone: customerData.phone || customerData.phoneNumber || 'Non spécifié',
      };

      downloadInvoicePDF(invoiceData, formattedCustomerData);
      toast.success('Téléchargement du PDF démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
      console.error('Erreur PDF:', error);
    }
  };

  // Gérer l'ouverture du scanner
  const openScanner = () => {
    navigate('?scan=true');
  };

  // Gérer la fermeture du scanner
  const closeScanner = () => {
    navigate(location.pathname);
  };

  // Gérer le scan d'un code-barres
  const handleScan = (barcode) => {
    // Rechercher le produit dans la base de données
    toast.success(`Produit scanné: ${barcode}`);
    
    // Rediriger vers la page de création de vente avec le produit scanné
    navigate(`/sales/new?product=${barcode}`);
  };

  // Gérer les actions
  const handleView = (sale) => {
    const payload = sale.__raw ? sale.__raw : sale;
    navigate(`/sales/${sale.id}`, { state: { sale: payload } });
  };

  const handleEdit = (sale) => {
    navigate(`/sales/${sale.id}/edit`);
  };

  const handleDelete = (saleId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette vente ?')) {
      // Ici, dans une application réelle, on appellerait une API pour supprimer la vente
      toast.success('Vente supprimée avec succès');
      // Si nous avions un état local des ventes, nous le mettrions à jour ici
    }
  };

  // Nous n'utilisons plus de données fictives - toutes les données viennent du contexte

  // Build real sales from context and normalize to display shape
  const customers = useMemo(() => getCustomers(), [getCustomers]);
  const salesFromContext = useMemo(() => getSales(), [getSales]);

  const realSalesDisplay = useMemo(() => {
    console.log('🔄 SalesPage: Using shared transformation utility');
    // Use the SHARED transformation utility - ensures consistency with SalesReports
    return transformSalesData(salesFromContext || [], customers);
  }, [salesFromContext, customers]);

  // Use only real sales data - no demo data for reports accuracy
  const allSales = [...realSalesDisplay];

  // Filtrer les ventes par terme de recherche et statut
  const filteredSales = allSales.filter(sale => {
    const matchesSearch = searchTerm === '' ||
      sale.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.customer.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;

    const matchesTab = activeTab === 'all' || sale.status === activeTab || sale.type === activeTab;

    return matchesSearch && matchesStatus && matchesTab;
  });

  const sales = filteredSales.slice((currentPage - 1) * salesPerPage, currentPage * salesPerPage);
  const totalSales = filteredSales.length;
  const totalPages = Math.ceil(totalSales / salesPerPage);

  // Statistiques des ventes - calculated from REAL data using shared utility
  const salesStats = useMemo(() => calculateSalesStats(realSalesDisplay), [realSalesDisplay]);
  
  const stats = [
    {
      title: 'Total des Ventes',
      value: `${Math.round(salesStats.totalAmount).toLocaleString('fr-DZ')} DA`,
      icon: BanknotesIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Nombre de Ventes',
      value: `${salesStats.totalSales}`,
      icon: BuildingStorefrontIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Panier Moyen',
      value: `${Math.round(salesStats.averageOrderValue).toLocaleString('fr-DZ')} DA`,
      icon: UsersIcon,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  // Répartition par type de vente - calculated from REAL data
  const salesDistribution = useMemo(() => {
    const distribution = {};
    realSalesDisplay.forEach(sale => {
      const type = sale.type || 'retail';
      if (!distribution[type]) {
        distribution[type] = { name: type, count: 0 };
      }
      distribution[type].count++;
    });
    
    return [
      { name: 'Vente au détail', count: distribution.retail?.count || 0, color: 'from-blue-400 to-blue-500' },
      { name: 'Vente en gros', count: distribution.wholesale?.count || 0, color: 'from-purple-400 to-purple-500' },
      { name: 'Vente en ligne', count: distribution.online?.count || 0, color: 'from-green-400 to-green-500' },
    ];
  }, [realSalesDisplay]);

  // Onglets
  const tabs = [
    { id: 'all', name: 'Toutes les Ventes', icon: DocumentTextIcon },
    { id: 'completed', name: 'Complétées', icon: CheckCircleIcon },
    { id: 'pending', name: 'En Attente', icon: ClockIcon },
    { id: 'cancelled', name: 'Annulées', icon: ExclamationTriangleIcon },
    { id: 'retail', name: 'Détail', icon: ShoppingBagIcon },
    { id: 'wholesale', name: 'En Gros', icon: ReceiptPercentIcon },
  ];

  return (
    <>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Ventes</h1>
            <p className="text-white/60">Gérez vos ventes et factures</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={openScanner}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center space-x-2"
              title="Scanner un produit pour vente rapide"
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>Scanner</span>
            </button>
            
            <Link
              to="/sales/new"
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nouvelle vente</span>
            </Link>
          </div>
        </div>

        {/* Routes */}
        <Routes>
          <Route path="/" element={
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                        <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                      </div>
                      <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Tabs Navigation */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setCurrentPage(1);
                      }}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Répartition par type de vente */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <h3 className="text-xl font-semibold text-white mb-4">
                  Répartition par Type de Vente
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {salesDistribution.map((item) => (
                    <div 
                      key={item.name}
                      className="bg-white/5 rounded-xl p-4 border border-white/10"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-white">{item.name}</h4>
                        <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                          {item.count}
                        </div>
                      </div>
                      <div className="w-full bg-white/10 h-3 rounded-full">
                        <div 
                          className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                          style={{ width: `${(item.count / allSales.length * 100).toFixed(1)}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-sm text-white/60">
                        {(item.count / allSales.length * 100).toFixed(1)}% des ventes
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Search and Filters */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  {/* Search */}
                  <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder="Rechercher une vente..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="flex items-center space-x-2">
                    <FunnelIcon className="h-5 w-5 text-white/50" />
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="completed">Complétées</option>
                      <option value="pending">En attente</option>
                      <option value="cancelled">Annulées</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Sales List */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
                {sales.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-center">
                    <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">Aucune vente trouvée</h3>
                    <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                    <Link
                      to="/sales/new"
                      className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Créer une vente</span>
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="px-6 py-4 text-left text-white font-semibold">N° Vente</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Client</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Montant</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Type</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                          {sales.map((sale) => (
                            <motion.tr
                              key={sale.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="hover:bg-white/5 transition-colors"
                            >
                              <td className="px-6 py-4 text-white font-mono text-sm">{sale.invoiceNumber}</td>
                              <td className="px-6 py-4 text-white font-medium">{sale.customer}</td>
                              <td className="px-6 py-4 text-white/80">{sale.date}</td>
                              <td className="px-6 py-4 text-white font-medium">{sale.amount}</td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  sale.status === 'completed' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : sale.status === 'pending'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {sale.status === 'completed' ? 'Complétée' : 
                                   sale.status === 'pending' ? 'En attente' : 'Annulée'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  sale.type === 'retail' 
                                    ? 'bg-blue-500/20 text-blue-400' 
                                    : sale.type === 'wholesale'
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : 'bg-green-500/20 text-green-400'
                                }`}>
                                  {sale.type === 'retail' ? 'Détail' : 
                                   sale.type === 'wholesale' ? 'En gros' : 'En ligne'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleView(sale)}
                                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                    title="Voir les détails"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDownloadPDF(sale)}
                                    className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                    title="Télécharger en PDF"
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleEdit(sale)}
                                    className="p-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors"
                                    title="Modifier"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(sale.id)}
                                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                    title="Supprimer"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                        <div className="text-sm text-white/60">
                          Affichage de {(currentPage - 1) * salesPerPage + 1} à {Math.min(currentPage * salesPerPage, totalSales)} sur {totalSales} ventes
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Précédent
                          </button>
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Suivant
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          } />
          
          {/* Routes pour la création et l'édition */}
          <Route path="/new" element={<SaleForm />} />
          <Route path="/:id" element={<SaleDetail />} />
          <Route path="/:id/edit" element={<SaleForm />} />
        </Routes>
      </div>

      {/* Scanner de code-barres modal - en dehors du composant Routes */}
      <BarcodeScanner 
        isOpen={isScannerOpen}
        onClose={closeScanner}
        onScan={handleScan}
        onError={(error) => toast.error(error)}
        mode="select"
        title="Scanner un produit pour la vente"
      />
    </>
  );
};

export default SalesPage; 